import styled from "styled-components";
import { SP_BREAKPOINT } from "../../../constants/app";
import { TableHeaderItem } from "../../../hooks/useTable";
import { TableHeader } from "../../molecules/table/TableHeader";
import { TableRow } from "../../molecules/table/TableRow";
import { useTable } from "./hook";
import { FileTypeNumber } from "../../../core/api/requests/file/download/types";

type Props<DataType extends { selected?: boolean; disabled?: boolean }> = {
  headers: TableHeaderItem<DataType>[];
  data: DataType[];
  selectable?: boolean;
  editable?: boolean;
  deletable?: boolean;
  downloadable?: boolean;
  enableCSVDownload?: boolean;
  stickyHeader?: boolean;
  defaultEmptyText?: string;
  enableHoverPopup?: boolean;
  disableDeleteButton?: boolean;
  onEdit?: (data: DataType) => void;
  onDelete?: (data: DataType) => void;
  onDownload?: (data: DataType, fileType: FileTypeNumber) => void;
  onCSVDownload?: (data: DataType, fileType: FileTypeNumber) => void;
  onBulkDelete?: () => void;
  onSelectAll?: (checked: boolean) => void;
  onSelect?: (data: DataType, checked: boolean) => void;
};

export const Table = <
  DataType extends { disabled?: boolean; selected?: boolean },
>({
  headers,
  data,
  selectable,
  editable,
  deletable,
  downloadable,
  stickyHeader,
  defaultEmptyText,
  disableDeleteButton,
  enableCSVDownload,
  enableHoverPopup,
  onEdit,
  onDelete,
  onDownload,
  onCSVDownload,
  onBulkDelete,
  onSelectAll,
  onSelect,
}: Props<DataType>) => {
  const { isSomeSelected } = useTable<DataType>(data);

  return (
    <>
      <StyledTable $stickyHeader={stickyHeader} $editable={editable}>
        <TableHeader<DataType>
          headers={headers}
          selectable={data.length > 0 ? selectable : false}
          editable={data.length > 0 ? editable : false}
          deletable={data.length > 0 ? deletable : false}
          onSelectAll={onSelectAll}
          onBulkDelete={onBulkDelete}
          disableDeleteButton={disableDeleteButton}
          isChecked={data.some((item) => item.selected && !item.disabled)}
          isSomeSelected={isSomeSelected}
        />

        <tbody>
          {data.length > 0 &&
            data.map((item, i) => (
              <TableRow
                key={`table-row-${(item as any).id ?? i}`}
                headers={headers}
                data={item}
                selectable={selectable}
                editable={editable}
                deletable={deletable}
                downloadable={downloadable}
                enableCSVDownload={enableCSVDownload}
                enableHoverPopup={enableHoverPopup}
                onEdit={onEdit}
                onSelect={onSelect}
                onDownload={onDownload}
                onCSVDownload={onCSVDownload}
                onDelete={onDelete}
              />
            ))}
          {data.length === 0 && (
            <tr className={`empty`}>
              <td colSpan={headers.length}>
                <StyledEmptyTableText $editable={editable}>
                  {defaultEmptyText ?? "アップロードされたファイルはありません"}
                </StyledEmptyTableText>
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </>
  );
};

const StyledTable = styled.table<{
  $stickyHeader?: boolean;
  $editable?: boolean;
}>`
  @media (max-width: ${SP_BREAKPOINT}) {
    ${({ $stickyHeader }) =>
      $stickyHeader
        ? "position: absolute; top: 0; left: 0; width: auto; height: auto; min-width: 100%;"
        : ""}
  }
  table-layout: fixed;
  thead {
    height: 48px;
    ${({ $stickyHeader }) =>
      $stickyHeader
        ? "position: sticky; top: 0; z-index: 1; background-color: white;"
        : ""}
    box-shadow: 0px 1px 0px rgba(204, 204, 204, 1);
    tr {
      ${({ $stickyHeader }) => ($stickyHeader ? "border: none;" : "")}
    }
  }

  tr {
    border-bottom: 1px solid #f2f2f2;
    &.empty {
      border-bottom: none;
    }
  }

  th,
  td {
    padding: ${({ $editable }) => ($editable ? "6px 16px" : "16px")};
    vertical-align: middle;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 100%;
    white-space: nowrap;
    width: fit-content;

    button {
      margin: 0 auto;
      height: 40px;
    }
  }
`;

const StyledEmptyTableText = styled.div<{ $editable?: boolean }>`
  color: #999999;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 200%;
  padding-top: ${({ $editable }) => ($editable ? "18px" : "10px")};
`;
